import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { signIn } from "../../lib/allauth";
import css from "./login.module.css";
import { useTranslation } from "react-i18next";
import logo from '../../img/headerLogoOneUpsc.png';

const FormWrap = () => {
  const [email, setEmail] = useState("");
  const [pasw, setPasw] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Social button effects
    const handleSocialClick = (e) => {
      // Create ripple effect
      const button = e.currentTarget;
      const ripple = document.createElement('span');
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;

      ripple.style.width = ripple.style.height = size + 'px';
      ripple.style.left = x + 'px';
      ripple.style.top = y + 'px';
      ripple.style.position = 'absolute';
      ripple.style.borderRadius = '50%';
      ripple.style.background = 'rgba(147, 51, 234, 0.3)';
      ripple.style.animation = 'ripple-effect 0.6s ease-out';
      ripple.style.pointerEvents = 'none';

      button.appendChild(ripple);

      setTimeout(() => ripple.remove(), 600);
    };

    const socialButtons = document.querySelectorAll(`.${css.socialBtn}`);
    socialButtons.forEach(btn => {
      btn.addEventListener('click', handleSocialClick);
    });

    return () => {
      socialButtons.forEach(btn => {
        btn.removeEventListener('click', handleSocialClick);
      });
    };
  }, []);

  const handleLogin = async () => {
    if (!email || !pasw) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: "Please fill in all the fields!",
      });
      return;
    }

    setIsSubmitting(true);
    const button = document.querySelector("#submitBtn");
   

    try {
      button.textContent = t("Loading...");
      const response = await signIn({ email: email, password: pasw });
      console.log("Login response:", response);

      if (response.meta.is_authenticated) {
        // Success animation
        if (button) {
          button.classList.add(css.success);
          button.textContent = '✓ ' + t("Login successful");
        }

        setTimeout(() => {
          window.location.href = "/";
        }, 1000);
      } else {
        // Error animation
        if (button) {
          button.classList.add(css.error);
          button.textContent = '✗ ' + t("Login failed");
        }

        Swal.fire({
          icon: "error",
          title: t("error"),
          text: "Invalid credentials.",
        });

        // Reset button after 3 seconds
        setTimeout(() => {
          if (button) {
            button.classList.remove(css.error);
            button.textContent = t("signin");
          }
        }, 3000);
      }
    } catch (error) {
      console.error("Login error details:", error);

      // Error animation
      if (button) {
        button.classList.add(css.error);
        button.textContent = '✗ ' + t("Login failed");
      }

      Swal.fire({
        icon: "error",
        title: t("error"),
        text: error.message || "An error occurred during login.",
      });

      // Reset button after 3 seconds
      setTimeout(() => {
        if (button) {
          button.classList.remove(css.error);
          button.textContent = t("signin");
        }
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={css.logoSection}>
        <div className={css.logoWrapper}>
          <img src={logo} alt="logo" />
        </div>
        <h2 className={css.brandTitle}>AI LINGUA</h2>
        <p className={css.brandSubtitle}>Speak The World</p>
      </div>

      <h1 className={css.title}>{t("signin")}</h1>
      <p className={css.formDescription}>{t("Enter your credentials to access your account")}</p>

      <div>
        <div className={css.formGroup}>
          <label className={css.label}>Email</label>
          <input
            type="email"
            className={css.input}
            placeholder="example@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className={css.formGroup}>
          <label className={css.label}>{t("password")}</label>
          <input
            type="password"
            className={css.input}
            placeholder="••••••••"
            value={pasw}
            onChange={(e) => setPasw(e.target.value)}
          />
        </div>

        <div className={css.forgotPassword}>
          <a href="#">{t("forgotpassword")}?</a>
        </div>

        <div className={css.divider}>
          <span>{t("oro")}</span>
        </div>

        <div className={css.socialAuth}>
          <button type="button" className={css.socialBtn}>
            <svg width="25" height="25" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          </button>
          <button type="button" className={css.socialBtn}>
            <svg width="25" height="25" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </button>
          <button type="button" className={css.socialBtn}>
            <svg width="25" height="25" viewBox="0 0 24 24">
              <rect width="10" height="10" x="2" y="2" fill="#F25022" />
              <rect width="10" height="10" x="12" y="2" fill="#7FBA00" />
              <rect width="10" height="10" x="2" y="12" fill="#00A4EF" />
              <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
            </svg>
          </button>
        </div>

        <button
          type="button"
          className={css.submitBtn}
          onClick={handleLogin}
          disabled={isSubmitting}
          id="submitBtn"
        >
          {t("signin")}
        </button>

        <p className={css.loginPrompt}>
          {t("account")} <a href="#" onClick={() => navigate("/signup")}>{t("register")}</a>
        </p>
      </div>
    </>
  );
};

export default FormWrap;
